import { type ReactNode } from 'react'

/**
 * Alternating image / text feature block used by every explore page.
 *
 * Visual + copy on alternating sides down the page builds rhythm —
 * each block reads as its own self-contained "here's a thing e404
 * does" beat. On mobile, the visual always stacks ABOVE the copy
 * (matches user scan order: image hooks, copy explains).
 *
 * Props deliberately match the rhythm of the explore page's content:
 *   - eyebrow: small all-caps marker so the block reads as part of a
 *     numbered feature series ("01 · MEMBERS", "02 · FEES", etc.)
 *   - headline: the punchy 4–8 word claim
 *   - copy: 2–3 sentences of explanation, plus optional bullets
 *   - bullets: tight sub-claims
 *   - visual: any ReactNode — usually a MockupFrame'd component
 *   - side: 'image-left' (default) or 'image-right' to alternate
 */
export function FeatureBlock({
  eyebrow,
  headline,
  copy,
  bullets,
  visual,
  side = 'image-left',
  accent = 'var(--color-primary, #3D6FFF)',
}: {
  eyebrow: string
  headline: ReactNode
  copy: ReactNode
  bullets?: string[]
  visual: ReactNode
  side?: 'image-left' | 'image-right'
  accent?: string
}) {
  const imageFirst = side === 'image-left'
  return (
    <section className="border-b border-border/40 px-6 py-20 md:py-28">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-16">
        {/* Visual — always renders first in DOM order so mobile gets
            it on top. Desktop reorders via `lg:order-*`. */}
        <div className={imageFirst ? 'lg:order-1' : 'lg:order-2'}>
          {visual}
        </div>

        {/* Copy */}
        <div className={imageFirst ? 'lg:order-2' : 'lg:order-1'}>
          <p
            className="font-display mb-4 text-xs font-bold tracking-[0.3em] uppercase"
            style={{ color: accent }}
          >
            {eyebrow}
          </p>
          <h2 className="font-display text-balance text-3xl leading-tight font-bold md:text-4xl lg:text-[2.6rem]">
            {headline}
          </h2>
          <div className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-fg-muted md:text-lg">
            {copy}
          </div>
          {bullets && bullets.length > 0 && (
            <ul className="mt-6 flex flex-col gap-3">
              {bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-sm text-fg-muted md:text-base"
                >
                  <span
                    aria-hidden
                    className="mt-2 inline-block size-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}
